import React, { useState, useEffect, useMemo } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const RegistrationsTab = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Default: last 7 days
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const fmt = d => d.toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(fmt(weekAgo));
  const [dateTo, setDateTo] = useState(fmt(today));

  const fetchData = async (from, to) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        from,
        to,
      });
      const res = await adminFetch(`${API_URL}/api/admin/registrations?${params}`);
      const data = await res.json();
      if (data.success) {
        setUsers(data.users);
      } else {
        setError(data.message || 'Failed to load');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => fetchData(dateFrom, dateTo);

  const setPreset = (days) => {
    const t = new Date();
    const f = new Date(t);
    f.setDate(f.getDate() - days);
    setDateFrom(fmt(f));
    setDateTo(fmt(t));
    fetchData(fmt(f), fmt(t));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = q
      ? users.filter(u =>
          (u.name + ' ' + u.family).toLowerCase().includes(q) ||
          u.username.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
        )
      : users;

    return [...list].sort((a, b) => {
      const va = a[sortField] || '';
      const vb = b[sortField] || '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, search, sortField, sortDir]);

  const sortIcon = (field) => {
    if (sortField !== field) return ' \u2195';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Stats
  const stats = useMemo(() => {
    const tgConnected = users.filter(u => !!u.tgId).length;
    const gdprDone = users.filter(u => u.consentGdpr).length;
    const canReceive = users.filter(u => !!u.tgId && u.consentGdpr).length;
    return { tgConnected, gdprDone, canReceive, total: users.length };
  }, [users]);

  return (
    <div>
      {/* Date range controls */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        <span style={{ color: '#6b7280' }}>&mdash;</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        <button onClick={handleApply} style={btnStyle}>Apply</button>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {[
            { label: 'Today', days: 0 },
            { label: '7d', days: 7 },
            { label: '30d', days: 30 },
            { label: '90d', days: 90 },
          ].map(p => (
            <button
              key={p.label}
              onClick={() => setPreset(p.days)}
              style={{ ...presetStyle }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, width: '200px', marginLeft: 'auto' }}
        />
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <StatBadge label="Total" value={stats.total} bg="#f3f4f6" color="#374151" />
        <StatBadge label="Telegram" value={`${stats.tgConnected}/${stats.total}`}
          bg={stats.tgConnected === stats.total ? '#d1fae5' : '#fef3c7'}
          color={stats.tgConnected === stats.total ? '#065f46' : '#92400e'} />
        <StatBadge label="GDPR" value={`${stats.gdprDone}/${stats.total}`}
          bg={stats.gdprDone === stats.total ? '#d1fae5' : '#fef3c7'}
          color={stats.gdprDone === stats.total ? '#065f46' : '#92400e'} />
        <StatBadge label="Can receive notifications" value={`${stats.canReceive}/${stats.total}`}
          bg={stats.canReceive === stats.total ? '#d1fae5' : '#fee2e2'}
          color={stats.canReceive === stats.total ? '#065f46' : '#991b1b'} />
      </div>

      {loading && <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}
      {error && <div style={{ padding: '1rem', color: '#dc2626' }}>{error}</div>}

      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={thStyle}>#</th>
                <th style={thSort} onClick={() => handleSort('name')}>Name{sortIcon('name')}</th>
                <th style={thSort} onClick={() => handleSort('username')}>Telegram{sortIcon('username')}</th>
                <th style={thSort} onClick={() => handleSort('createdAt')}>Registered{sortIcon('createdAt')}</th>
                <th style={thSort} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
                <th style={thStyle}>TG</th>
                <th style={thStyle}>GDPR</th>
                <th style={thStyle}>Notifications</th>
                <th style={thSort} onClick={() => handleSort('nextWeekStatus')}>Next Week{sortIcon('nextWeekStatus')}</th>
                <th style={thStyle}>Community</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => {
                const hasTg = !!u.tgId;
                const canNotify = hasTg && u.consentGdpr;
                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={tdStyle}>
                      {u.username ? (
                        <a href={`/profile/${u.username}`} target="_blank" rel="noopener noreferrer"
                          style={{ color: '#111827', textDecoration: 'none' }}
                          onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.target.style.textDecoration = 'none'}>
                          {u.name} {u.family}
                        </a>
                      ) : (
                        <>{u.name} {u.family}</>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {u.username ? (
                        <a href={`https://t.me/${u.username}`} target="_blank" rel="noopener noreferrer"
                          style={{ color: '#4f46e5', textDecoration: 'none' }}>@{u.username}</a>
                      ) : u.email ? (
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{u.email}</span>
                      ) : (
                        <span style={{ color: '#d1d5db' }}>-</span>
                      )}
                    </td>
                    <td style={tdStyle}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                    <td style={tdStyle}>
                      <span style={{ ...pillStyle, ...statusColor(u.status) }}>{u.status}</span>
                    </td>
                    <td style={tdStyle}>{hasTg ? checkMark : crossMark}</td>
                    <td style={tdStyle}>{u.consentGdpr ? checkMark : crossMark}</td>
                    <td style={tdStyle}>
                      {canNotify
                        ? <span style={{ color: '#059669', fontWeight: 600 }}>Ready</span>
                        : <span style={{ color: '#dc2626', fontWeight: 600 }}>
                            {!hasTg && !u.consentGdpr ? 'No TG + GDPR' : !hasTg ? 'No TG' : 'No GDPR'}
                          </span>
                      }
                    </td>
                    <td style={tdStyle}>
                      {u.nextWeekStatus ? (
                        <span style={{
                          ...pillStyle,
                          background: u.nextWeekStatus === 'Active' ? '#d1fae5' : '#f3f4f6',
                          color: u.nextWeekStatus === 'Active' ? '#065f46' : '#6b7280',
                        }}>{u.nextWeekStatus}</span>
                      ) : <span style={{ color: '#d1d5db' }}>-</span>}
                    </td>
                    <td style={tdStyle}>{u.community[0] || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              {search ? 'No matches' : 'No registrations in this period'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatBadge = ({ label, value, bg, color }) => (
  <div style={{
    padding: '0.5rem 1rem', borderRadius: '8px', background: bg,
    display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem',
  }}>
    <span style={{ color: '#6b7280' }}>{label}:</span>
    <span style={{ fontWeight: 700, color }}>{value}</span>
  </div>
);

const checkMark = <span style={{ color: '#059669', fontSize: '1.1rem' }}>&#10003;</span>;
const crossMark = <span style={{ color: '#dc2626', fontSize: '1.1rem' }}>&#10007;</span>;

const thStyle = { padding: '0.5rem 0.75rem', fontWeight: 600, whiteSpace: 'nowrap' };
const thSort = { ...thStyle, cursor: 'pointer' };
const tdStyle = { padding: '0.5rem 0.75rem' };
const pillStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 500 };

const inputStyle = {
  padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem',
};
const btnStyle = {
  padding: '0.4rem 1rem', background: '#4f46e5', color: '#fff', border: 'none',
  borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
};
const presetStyle = {
  padding: '0.3rem 0.6rem', background: '#f3f4f6', border: '1px solid #e5e7eb',
  borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', color: '#374151',
};

function statusColor(status) {
  switch (status) {
    case 'Admin': return { background: '#fef3c7', color: '#92400e' };
    case 'PRO': return { background: '#dbeafe', color: '#1e40af' };
    case 'Premium': return { background: '#ede9fe', color: '#5b21b6' };
    case 'Free': return { background: '#f3f4f6', color: '#374151' };
    case 'EarlyBird': return { background: '#d1fae5', color: '#065f46' };
    default: return { background: '#f3f4f6', color: '#374151' };
  }
}

export default RegistrationsTab;
